import type { InferRequestType } from 'hono/client';
import type { api } from '@/lib/api';

// Fabrique de clés TanStack Query (`frontend.md` § 3). Chaque ressource est ajoutée par sa tâche :
// scans (T11), clusters (T15), opportunities (T23), experiments (T27), dashboard (T29),
// calibration (T30). T28 : settings, scoring-configs, exclusions, costs, jobs, audit, admin.

/** Filtres d'une liste de seeds tels qu'envoyés à l'API (query string typée par la route). */
export type SeedsListFilters = InferRequestType<typeof api.seeds.$get>['query'];
/** Filtres de `GET /scans` (query string typée par la route). */
export type ScansListFilters = InferRequestType<typeof api.scans.$get>['query'];
/** Corps de `POST /scans/estimate`. */
export type ScanEstimateInput = InferRequestType<typeof api.scans.estimate.$post>['json'];
export type ExclusionsFilters = InferRequestType<typeof api.exclusions.$get>['query'];
export type LedgerFilters = InferRequestType<typeof api.costs.ledger.$get>['query'];
export type AuditFilters = InferRequestType<typeof api.audit.$get>['query'];
/** Filtres de `GET /clusters` (query string typée par la route). */
export type ClustersListFilters = InferRequestType<typeof api.clusters.$get>['query'];
/** Filtres de `GET /keywords` (query string typée par la route). */
export type KeywordsListFilters = InferRequestType<typeof api.keywords.$get>['query'];
/** Filtres de `GET /opportunities` (query string typée par la route). */
export type OpportunitiesListFilters = InferRequestType<typeof api.opportunities.$get>['query'];
/** Filtres de `GET /experiments` (query string typée par la route). */
export type ExperimentsListFilters = InferRequestType<typeof api.experiments.$get>['query'];

export const qk = {
  me: ['me'] as const,
  markets: ['markets'] as const,
  /**
   * `GET /dashboard`, partitionné par marché. Sans argument, la clé est le **préfixe**
   * `['dashboard']` : `invalidateQueries({ queryKey: qk.dashboard() })` touche tous les marchés
   * (une clé `['dashboard', undefined]` ne correspondrait à aucune requête, T29).
   */
  dashboard: (market?: string) =>
    market === undefined ? (['dashboard'] as const) : (['dashboard', market] as const),
  opportunities: {
    all: ['opportunities'] as const,
    /** Nombre d'opportunités concernées par un rescoring (`GET /opportunities?pageSize=1`). */
    rescoreCount: ['opportunities', 'rescore-count'] as const,
    list: (filters: OpportunitiesListFilters) => ['opportunities', 'list', filters] as const,
    detail: (id: string) => ['opportunities', 'detail', id] as const,
    events: (id: string) => ['opportunities', 'detail', id, 'events'] as const,
    analysis: (id: string, analysisId: string) =>
      ['opportunities', 'detail', id, 'analysis', analysisId] as const,
  },
  seeds: {
    all: ['seeds'] as const,
    list: (filters: SeedsListFilters) => ['seeds', 'list', filters] as const,
  },
  scans: {
    all: ['scans'] as const,
    list: (filters: ScansListFilters) => ['scans', 'list', filters] as const,
    detail: (id: string) => ['scans', 'detail', id] as const,
    events: (id: string) => ['scans', 'detail', id, 'events'] as const,
    results: (id: string) => ['scans', 'detail', id, 'results'] as const,
    funnel: (id: string) => ['scans', 'detail', id, 'funnel'] as const,
    estimate: (payload: ScanEstimateInput) => ['scans', 'estimate', payload] as const,
  },
  settings: ['settings'] as const,
  scoringConfigs: ['scoring-configs'] as const,
  exclusions: {
    all: ['exclusions'] as const,
    list: (filters: ExclusionsFilters) => ['exclusions', 'list', filters] as const,
  },
  clusters: {
    all: ['clusters'] as const,
    list: (filters: ClustersListFilters) => ['clusters', 'list', filters] as const,
    detail: (id: string) => ['clusters', 'detail', id] as const,
  },
  keywords: {
    all: ['keywords'] as const,
    list: (filters: KeywordsListFilters) => ['keywords', 'list', filters] as const,
  },
  experiments: {
    all: ['experiments'] as const,
    list: (filters: ExperimentsListFilters) => ['experiments', 'list', filters] as const,
    detail: (id: string) => ['experiments', 'detail', id] as const,
    brief: (id: string) => ['experiments', 'detail', id, 'brief'] as const,
  },
  costs: {
    all: ['costs'] as const,
    summary: (month: string) => ['costs', 'summary', month] as const,
    ledger: (filters: LedgerFilters) => ['costs', 'ledger', filters] as const,
  },
  jobs: (id: string) => ['jobs', id] as const,
  admin: {
    users: ['admin', 'users'] as const,
    invitations: ['admin', 'invitations'] as const,
  },
  audit: (filters: AuditFilters) => ['audit', filters] as const,
  /** `GET /calibration` : écran non partitionné par marché, une seule clé. */
  calibration: ['calibration'] as const,
  /** `GET /users` : sélecteurs d'owner (filtre de liste, assignation). */
  users: ['users'] as const,
} as const;
